const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-950 py-16 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <div className="text-blue-400 font-mono text-sm mb-3">
            // Legal
          </div>
          <h1 className="text-4xl font-bold text-blue-200 mb-3">
            Terms of Service
          </h1>
          <p className="text-gray-500 text-sm">
            Last updated: May 9, 2026
          </p>
        </div>

        <div className="space-y-10 text-gray-400 leading-relaxed">

          {/* Intro */}
          <section>
            <p className="text-gray-300">
              These Terms of Service ("Terms") govern your use of
              ScaffoldBridge ("Service"), operated by RenegadeRegime
              ("we", "our", or "us"). By using ScaffoldBridge,
              you agree to these Terms.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using ScaffoldBridge, you confirm that
              you are at least 13 years old, have read and understood
              these Terms, and agree to be bound by them. If you are
              using ScaffoldBridge on behalf of an organization, you
              represent that you have authority to bind that organization
              to these Terms.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              2. Description of Service
            </h2>
            <p className="mb-3">
              ScaffoldBridge is a SaaS platform that generates
              production-ready .NET 8 and Java Spring Boot REST API
              projects from SQL schemas or OpenAPI specifications.
              The Service includes:
            </p>
            <ul className="space-y-2">
              {[
                'Code generation from SQL and OpenAPI schemas',
                'Visual API builder interface',
                'GitHub push and delivery',
                'Docker image generation',
                'CI/CD pipeline configuration',
                'Schema save and load functionality',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1 shrink-0">→</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              3. Account Registration
            </h2>
            <p className="mb-3">
              To use ScaffoldBridge you must create an account. You agree to:
            </p>
            <ul className="space-y-2">
              {[
                'Provide accurate and complete registration information',
                'Maintain the security of your account credentials',
                'Notify us immediately of any unauthorized access',
                'Accept responsibility for all activity under your account',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1 shrink-0">→</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              4. Subscription Plans and Billing
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Free Plan
                </h3>
                <p>
                  The free plan includes 5 generations per month
                  with limited features. No credit card required.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Pro Plan
                </h3>
                <p>
                  The Pro plan is billed at $49.00 per month. You
                  will be charged on a recurring monthly basis until
                  you cancel.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Billing
                </h3>
                <p>
                  Payments are processed by Stripe. By subscribing,
                  you authorize us to charge your payment method on a
                  recurring basis. All fees are non-refundable except
                  where required by law.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Cancellation
                </h3>
                <p>
                  You may cancel your subscription at any time.
                  Cancellation takes effect at the end of the current
                  billing period. You retain Pro access until the
                  period ends.
                </p>
              </div>
            </div>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              5. Acceptable Use
            </h2>
            <p className="mb-3">You agree NOT to:</p>
            <ul className="space-y-2">
              {[
                'Use the Service for any illegal purpose',
                'Attempt to reverse engineer or copy the platform',
                'Share your account with others',
                'Use automated tools to scrape or abuse the API',
                'Submit schemas containing malicious code',
                'Resell or redistribute generated code as a competing service',
                'Circumvent usage limits or restrictions',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-red-500 mt-1 shrink-0">✗</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              6. Intellectual Property
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Your Content
                </h3>
                <p>
                  You retain ownership of all schemas and specifications
                  you submit. You grant us a limited license to process
                  your schemas solely to provide the Service.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Generated Code
                </h3>
                <p>
                  Code generated by ScaffoldBridge from your schemas
                  is yours to use freely in any project, commercial
                  or otherwise. No attribution required.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Our Platform
                </h3>
                <p>
                  ScaffoldBridge's platform, brand, and underlying
                  technology remain our exclusive property. You may
                  not copy, modify, or create derivative works of
                  our platform.
                </p>
              </div>
            </div>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              7. Disclaimer of Warranties
            </h2>
            <div className="bg-gray-900 border border-gray-800
                            rounded-xl p-4">
              <p className="text-sm">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES
                OF ANY KIND. WE DO NOT WARRANT THAT THE SERVICE WILL
                BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES.
                WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
                INCLUDING MERCHANTABILITY AND FITNESS FOR A
                PARTICULAR PURPOSE.
              </p>
            </div>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              8. Limitation of Liability
            </h2>
            <div className="bg-gray-900 border border-gray-800
                            rounded-xl p-4">
              <p className="text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCAFFOLDBRIDGE
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                LOSS OF PROFITS OR DATA. OUR TOTAL LIABILITY SHALL
                NOT EXCEED THE AMOUNT YOU PAID IN THE LAST 12 MONTHS.
              </p>
            </div>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              9. Termination
            </h2>
            <p className="mb-3">
              We may suspend or terminate your account if you:
            </p>
            <ul className="space-y-2">
              {[
                'Violate these Terms of Service',
                'Engage in fraudulent or abusive behavior',
                'Fail to pay subscription fees',
                'Are required by law',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1 shrink-0">→</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3">
              You may delete your account at any time from the
              billing settings page.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              10. Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the United States.
              Any disputes shall be resolved in the courts of the
              United States. If any provision of these Terms is found
              unenforceable, the remaining provisions remain in effect.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              11. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time.
              We will notify you of material changes via email or
              platform notice. Continued use of the Service after
              changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-900 border border-gray-800
                              rounded-2xl p-6">
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              Contact Us
            </h2>
            <p className="mb-3">
              For questions about these Terms:
            </p>
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-gray-300 font-medium">Email: </span>
                <a href="mailto:support@scaffoldbridge.io"
                   className="text-blue-400 hover:text-blue-300">
                  support@scaffoldbridge.io
                </a>
              </p>
              <p>
                <span className="text-gray-300 font-medium">Website: </span>
                <a href="https://scaffoldbridge.io"
                   className="text-blue-400 hover:text-blue-300">
                  scaffoldbridge.io
                </a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsPage;